/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `jump` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class JumpMesg : Mesg {
    public constructor() : super("Jump", Profile.MesgNum.JUMP)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** distance, in m. */
    public var distance: Float?
        get() = getFieldValue(DISTANCE_FIELD_NUM) as? Float
        set(value) = setFieldValue(DISTANCE_FIELD_NUM, value)

    /** height, in m. */
    public var height: Float?
        get() = getFieldValue(HEIGHT_FIELD_NUM) as? Float
        set(value) = setFieldValue(HEIGHT_FIELD_NUM, value)

    /** rotations. */
    public var rotations: UByte?
        get() = getFieldValue(ROTATIONS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(ROTATIONS_FIELD_NUM, value)

    /** hang_time, in s. */
    public var hangTime: Float?
        get() = getFieldValue(HANG_TIME_FIELD_NUM) as? Float
        set(value) = setFieldValue(HANG_TIME_FIELD_NUM, value)

    /** score. A score for a jump calculated based on hang time, rotations, and distance. */
    public var score: Float?
        get() = getFieldValue(SCORE_FIELD_NUM) as? Float
        set(value) = setFieldValue(SCORE_FIELD_NUM, value)

    /** position_lat, in semicircles. */
    public var positionLat: Int?
        get() = getFieldValue(POSITION_LAT_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LAT_FIELD_NUM, value)

    /** position_long, in semicircles. */
    public var positionLong: Int?
        get() = getFieldValue(POSITION_LONG_FIELD_NUM) as? Int
        set(value) = setFieldValue(POSITION_LONG_FIELD_NUM, value)

    /** speed, in m/s. */
    public var speed: Double?
        get() = getFieldValue(SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(SPEED_FIELD_NUM, value)

    /** enhanced_speed, in m/s. */
    public var enhancedSpeed: Double?
        get() = getFieldValue(ENHANCED_SPEED_FIELD_NUM) as? Double
        set(value) = setFieldValue(ENHANCED_SPEED_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val DISTANCE_FIELD_NUM: UByte = 0u
        public const val HEIGHT_FIELD_NUM: UByte = 1u
        public const val ROTATIONS_FIELD_NUM: UByte = 2u
        public const val HANG_TIME_FIELD_NUM: UByte = 3u
        public const val SCORE_FIELD_NUM: UByte = 4u
        public const val POSITION_LAT_FIELD_NUM: UByte = 5u
        public const val POSITION_LONG_FIELD_NUM: UByte = 6u
        public const val SPEED_FIELD_NUM: UByte = 7u
        public const val ENHANCED_SPEED_FIELD_NUM: UByte = 8u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("Distance", 0u, BaseType.FLOAT32, 1.0, 0.0, "m", false)
            fields[1] = Field("Height", 1u, BaseType.FLOAT32, 1.0, 0.0, "m", false)
            fields[2] = Field("Rotations", 2u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[3] = Field("HangTime", 3u, BaseType.FLOAT32, 1.0, 0.0, "s", false)
            fields[4] = Field("Score", 4u, BaseType.FLOAT32, 1.0, 0.0, "", false)
            fields[5] = Field("PositionLat", 5u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[6] = Field("PositionLong", 6u, BaseType.SINT32, 1.0, 0.0, "semicircles", false)
            fields[7] = Field("Speed", 7u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
                .addComponent(FieldComponent(8u, false, 16, 1000.0, 0.0))
            fields[8] = Field("EnhancedSpeed", 8u, BaseType.UINT32, 1000.0, 0.0, "m/s", false)
            return fields
        }
    }
}
